import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ACTIONS from "../../store/action";
import { selectUser } from "../../store/selector";
import { useGetSetting } from "../../hooks/getSetting";
import { CustomLayout } from "./Styles";
import { ChatSider } from "./ChatSider";
import { ChatWidgetLayout } from "./ChatWidgetLayout";
import { PENDO_EVENTS } from "./Constants";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";

const AIChatViewComponent = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const history = useHistory();
  const location = useLocation();
  const { trackEvent } = useDashboardBucketsTrackingEvent();
  const [displayAIChatBot] = useGetSetting(["fe.show-ai-chat-bot"]);

  const userId = user?.oktaId;
  const [collapsed, setCollapsed] = useState(false);
  const { conversationId } = useParams();
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false);
  const [conversationIdToLoadHistory, setConversationIdToLoadHistory] = useState(null);
  const [isNewConversation, setIsNewConversation] = useState(false);

  const [clearChatHistory, setClearChatHistory] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [shouldRefreshConversationList, setShouldRefreshConversationList] = useState(false);

  useEffect(() => {
    if (!conversationId) {
      setClearChatHistory(true);
      setIsNewConversation(false);
    } else if (userId && conversationId && !isNewConversation) {
      setShouldLoadHistory(true);
      setConversationIdToLoadHistory(conversationId);
    }
  }, [user, conversationId]);

  const handleConversationListRefreshed = () => {
    setShouldRefreshConversationList(false);
  };

  const navigateToNewChat = useCallback(() => {
    setClearChatHistory(true);
    setPendingNavigation(true);
  }, []);

  useEffect(() => {
    if (clearChatHistory && pendingNavigation) {
      dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
      history.push({
        pathname: `/ai-chat-bot/new`,
        state: { from: location.pathname }
      });
      setPendingNavigation(false);
      setClearChatHistory(false);
    }
  }, [clearChatHistory, pendingNavigation]);

  const handleSelectedConversationFromHistory = useCallback((newConversationId) => {
    trackEvent(PENDO_EVENTS.RECENT_CHAT_SELECTED, { conversationId: newConversationId });
    setClearChatHistory(false);
    dispatch(ACTIONS.setAiChatBotIntroPanelShown(false));
    history.push({
      pathname: `/ai-chat-bot/conversations/${newConversationId}/messages`,
      state: { from: location.pathname }
    });
  }, []);

  const handleAfterLoadingSelectedConversation = useCallback(() => {
    setShouldLoadHistory(false);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const handleAfterConversationCreated = useCallback((newConversationId) => {
    setIsNewConversation(true);
    history.replace({
      pathname: `/ai-chat-bot/conversations/${newConversationId}/messages`,
      state: { from: location.pathname }
    });
    setShouldRefreshConversationList(true);
    setIsNewConversation(false);
  }, []);

  const handleChatCleared = useCallback(() => {
    setClearChatHistory(false);
  }, []);

  const handleStreamingStateChange = useCallback((isStreaming) => {
    dispatch(ACTIONS.setChatStreaming(isStreaming));
  }, []);

  return displayAIChatBot ? (
    <CustomLayout>
      <ChatSider
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        userId={userId}
        activeConversationId={conversationId}
        handleConversationChange={handleSelectedConversationFromHistory}
        shouldRefreshConversationList={shouldRefreshConversationList}
        onConversationListRefreshed={handleConversationListRefreshed}
        onNewChat={navigateToNewChat}
        headerProps={{
          onNewChat: navigateToNewChat,
          collapsed: collapsed,
          onCollapsed: toggleCollapsed
        }}
      />
      <ChatWidgetLayout
        selectedConversationId={conversationIdToLoadHistory}
        collapsed={collapsed}
        shouldLoadHistory={shouldLoadHistory}
        clearChatHistory={clearChatHistory}
        handleAfterConversationCreated={handleAfterConversationCreated}
        handleAfterLoadingSelectedConversation={handleAfterLoadingSelectedConversation}
        onChatCleared={handleChatCleared}
        onStreamingStateChange={handleStreamingStateChange}
        headerProps={{
          onNewChat: navigateToNewChat,
          collapsed: collapsed,
          onCollapsed: toggleCollapsed
        }}
      />
    </CustomLayout>
  ) : null;
};

export { AIChatViewComponent as AIChatView };
